import React, { lazy, Suspense, useRef, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from 'shared/ui/theme';
import type { LinkPage } from 'features/public-pages/public/publicApi';
import { LINK_REPORT_ID, linkVisualUid, showPreviewPage, type LinkResult } from 'features/visuals/public/data/linkReads';
import { onRenderObservations, renderErrors } from 'features/visuals/inspector/renderFeedback';
import { useMountEffect, useSyncOutside } from 'shared/hooks';
import { PageRenderer } from './PageFrame';
import { FrameFeedback, Interaction, Page, Results } from './types';

type Snapshot = { kind?: 'html'; reportId: string; revision: number; page: Page; results: Results;
  assets: {ready: boolean; urls: Record<string,string>; errors: FrameFeedback['errors']} };
type NativeSnapshot = { kind: 'native'; revision: string; page: {id: string; name: string}; document: LinkPage; results: Record<string, LinkResult> };
const NativeLinkPage = lazy(() => import('features/public-pages/NativeLinkPage'));
const READ_KINDS = ['declarativeChart', 'dataCard', 'dataTable', 'matrix'];
const channel = '/html-preview/';
const noop = () => {};

async function previewQuery(payload: object, signal: AbortSignal) {
  const response = await fetch(channel+'query', {method:'POST',credentials:'omit',signal,
    headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
  const result = await response.json();
  if (!response.ok || result.error) throw new Error(result.error || 'Preview query failed.');
  return result;
}

function PreviewDocument({snapshot}: {snapshot: Snapshot}) {
  const [runtime,setRuntime] = useState<{results:Results; interaction:Interaction|null; version:number; pending:boolean; error:string}>(
    {results:snapshot.results,interaction:null,version:0,pending:false,error:''});
  const generation = useRef(0);
  const request = useRef(new AbortController());
  const lastFeedback = useRef<FrameFeedback>();
  useMountEffect(()=>{
    request.current=new AbortController();
    return ()=>{generation.current++;request.current.abort();};
  });
  const changeInteraction = async (interaction: Interaction|null) => {
    const version=++generation.current;
    request.current.abort();request.current=new AbortController();
    const signal=request.current.signal;
    setRuntime(current=>({...current,interaction,version,pending:true,error:''}));
    try {
      const result=await previewQuery({kind:'interaction',interaction},signal);
      if (!result.results || typeof result.results !== 'object' || Array.isArray(result.results))
        throw new Error('Preview filtering returned an invalid result batch.');
      if (generation.current===version) setRuntime(current=>{
        const results={...result.results};
        // Keep the source visual's baseline object/DOM, just like the live
        // workspace. A click must not rebuild its marks or reset a source table.
        const source=snapshot.page.visuals.find(v=>v.id===interaction?.visualId);
        source?.dataIds.forEach(key=>{
          const slot=source.id+'/'+key, baseline=snapshot.results[slot] || snapshot.results[key];
          if (baseline && !results[slot]?.error) results[slot]=baseline;
        });
        return {...current,results,pending:false};
      });
    } catch (error) {
      if (!signal.aborted && generation.current===version)
        setRuntime(current=>({...current,pending:false,error:error instanceof Error ? error.message : String(error)}));
    }
  };
  const publish = (value:FrameFeedback) => {
    // Only the credential-free host can publish evidence read by the backend.
    (window as any).__unityPagePreview = {protocolVersion:3,feedback:{...value,
      dataStatus:runtime.pending ? 'loading' : value.dataStatus,
      errors:runtime.error ? [...value.errors,{phase:'query',message:runtime.error}] : value.errors},updatedAt:Date.now()};
  };
  const feedback = (value:FrameFeedback) => {lastFeedback.current=value;publish(value);};
  useSyncOutside(()=>{if(lastFeedback.current) publish(lastFeedback.current);},[runtime.pending,runtime.error]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadingSlots = runtime.pending ? snapshot.page.visuals.filter(v=>v.id!==runtime.interaction?.visualId)
    .flatMap(v=>v.dataIds.map(key=>v.id+'/'+key)) : [];
  return <div style={{position:'fixed',inset:0}}>
    <PageRenderer reportId={snapshot.reportId} page={snapshot.page} revision={snapshot.revision}
      assets={snapshot.assets} results={runtime.results} editing={false} inspecting={false} ready={!runtime.pending}
      selection={null} onSelect={noop} onFeedback={feedback} onNavigate={noop}
      interaction={runtime.interaction} onInteraction={changeInteraction} onClearInteraction={()=>changeInteraction(null)}
      loadingSlots={loadingSlots} contextKey={snapshot.revision+':'+runtime.version}
      onQuery={(visualId,dataId,options)=>previewQuery({visualId,dataId,options,interaction:runtime.interaction},request.current.signal)} />
  </div>;
}

/** A native page's reads answer from its snapshot, shown before the canvas renders. The host is ready when every visual with a query
 * has had its read answered (the backend then waits for the canvas to settle). A visual that fails to render (its renderer's error, as
 * the visual shows it) is in the errors by visualUid, as an HTML page's are; each change is published again. */
function showNativeSnapshot(snapshot: NativeSnapshot) {
  const pending = new Set(snapshot.document.visuals.filter(v => READ_KINDS.includes(v.type)).map(v => v.visualUid));
  const publish = () => {
    // A failed chart and the error state it shows observe the same message: one error per visual and message.
    const errors = Array.from(new Map(renderErrors(LINK_REPORT_ID).map(item => [item.id + '\n' + item.message,
      {id: linkVisualUid(item.id), phase: 'render', message: item.message}])).values());
    (window as any).__unityPagePreview = {protocolVersion:3, updatedAt:Date.now(),
      feedback:{revision:snapshot.revision, dataStatus:pending.size ? 'loading' : 'ready', errors}};
  };
  showPreviewPage(snapshot.document, snapshot.results, uid => { pending.delete(uid); publish(); });
  onRenderObservations(publish);
  publish();
}

/** A native page on the canvas public links use, read-only, from the snapshot alone: no read, paging, menu or write. */
function NativePreview({snapshot}: {snapshot: NativeSnapshot}) {
  return <ThemeProvider theme={theme}><CssBaseline />
    <div data-unity-native-preview="" style={{position:'fixed', inset:0}}>
      <Suspense fallback={null}><NativeLinkPage snapshot={{kind:'native', loadId:1, document:snapshot.document}} bottomOffset={0} /></Suspense>
    </div>
  </ThemeProvider>;
}

/** This host has no report API or login credential. Only the backend's isolated
 * browser supplies these intercepted requests; opening the URL exposes no data.
 * Generated HTML stays in PageRenderer's opaque sandbox, never in this host.
 */
export default function PagePreview() {
  const [snapshot, setSnapshot] = useState<Snapshot | NativeSnapshot>();
  const [error, setError] = useState('');
  useMountEffect(() => {
    const abort = new AbortController();
    fetch(channel + 'snapshot', {signal: abort.signal, credentials: 'omit'}).then(response => {
      if (!response.ok || !response.headers.get('Content-Type')?.includes('application/json'))
        throw new Error('Open the report to use Unity. This preview host is reserved for the assistant.');
      return response.json();
    }).then((value: Snapshot | NativeSnapshot) => {
      if (value.kind === 'native') showNativeSnapshot(value);
      setSnapshot(value);
    }).catch(err => {
      if (!abort.signal.aborted) setError(err.message);
    });
    return () => abort.abort();
  });
  if (!snapshot) return <div role="status">{error || 'Preparing page preview…'}</div>;
  return snapshot.kind === 'native' ? <NativePreview snapshot={snapshot} /> : <PreviewDocument snapshot={snapshot} />;
}
